// ** Modules
import * as React from 'react';
import { useId } from 'react';
import Image from 'next/image';
import { motion, useReducedMotion } from 'framer-motion';

// ** UI
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Card from '@mui/material/Card';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha, useTheme } from '@mui/material/styles';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// ** Components
import StrapiImageRenderer from '@/components/strapi/StrapiImageRenderer';

// ** Utils
import { formatDisplayUrl } from '@/utils/shared/stringUtils';

// ** Types
import type { AppContext } from '@/types/contextTypes';

export type PartnerCardModel = {
	id: string;
	headline: string;
	subline: string;
	/**
	 * Optional. When no logo is provided, the hover swap is disabled and the
	 * contact details (address, phone, email, website) are rendered statically
	 * in place of the image area.
	 */
	logoSrc?: string;
	logoAlt?: string;
	address?: string;
	phone?: string;
	email?: string;
	website?: string;
	host: AppContext['host'];
};

export type PartnerCardProps = PartnerCardModel;

const clampText = (value: string, maxChars = 60) => {
	if (!value || typeof value !== 'string') return '';

	if (value.length <= maxChars) return value;
	return `${value.slice(0, maxChars).trimEnd()}...`;
};

const CONTACT_LABELS: Record<string, string> = {
	phone: 'Telefon',
	email: 'E-Mail',
	website: 'Website'
};

const getContactHref = (key: string, value: string) => {
	if (key === 'phone') return `tel:${value.replace(/\s/g, '')}`;
	if (key === 'email') return `mailto:${value}`;
	return /^https?:\/\//.test(value) ? value : `https://${value}`;
};

const PartnerCard = ({
	headline,
	subline,
	logoSrc,
	logoAlt = '',
	address,
	phone,
	email,
	website,
	host
}: PartnerCardProps) => {
	const theme = useTheme();
	const panelId = useId();
	const headingId = useId();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const shouldReduceMotion = useReducedMotion();
	const [isHovered, setIsHovered] = React.useState(false);
	const [isMobileExpanded, setIsMobileExpanded] = React.useState(false);
	const hasOverlayContent = Boolean(address || phone || email || website);
	const hasImage = Boolean(logoSrc);
	const isHoverEnabled = hasImage && hasOverlayContent;
	const smoothEase: [number, number, number, number] = [0.22, 1, 0.36, 1];

	const clampedHeadline = React.useMemo(() => clampText(headline, 60), [headline]);
	const clampedSubline = React.useMemo(() => clampText(subline, 60), [subline]);

	React.useEffect(() => {
		if (process.env.NODE_ENV === 'production') return;
		const headlineLength = headline?.length || 0;
		if (headlineLength > 60) {
			console.warn(`[PartnerCard] Headline exceeds 60 characters (${headlineLength}).`);
		}
	}, [headline]);

	const overlayRows = React.useMemo(() => {
		const rows = [
			{ key: 'phone', value: phone, icon: PhoneOutlinedIcon },
			{ key: 'email', value: email, icon: MailOutlinedIcon },
			{
				key: 'website',
				value: website,
				icon: LanguageOutlinedIcon
			}
		];

		return rows
			.filter((row): row is { key: string; value: string; icon: any } => Boolean(row.value))
			.map((row) => ({
				...row,
				displayText: row.key === 'website' ? formatDisplayUrl(row.value) : row.value
			}));
	}, [phone, email, website]);

	/**
	 * Shared Link Renderer
	 * Handles formatted display text and "open in new tab" for websites and email
	 */
	const renderContactLink = (key: string, value: string, displayText: string, IconComponent: any) => {
		const opensInNewTab = key === 'website' || key === 'email';

		return (
			<Box
				component="a"
				href={getContactHref(key, value)}
				target={opensInNewTab ? '_blank' : undefined}
				rel={opensInNewTab ? 'noopener noreferrer' : undefined}
				aria-label={`${CONTACT_LABELS[key]}: ${value}`}
				sx={{
					display: 'flex',
					alignItems: isMobile ? 'center' : 'flex-start',
					gap: 4,
					textDecoration: 'none',
					color: isMobile ? 'common.black' : 'inherit',
					'&:focusVisible': {
						outline: (theme) => `2px solid ${alpha(theme.palette.common.black, 0.35)}`,
						outlineOffset: 2,
						borderRadius: 1
					}
				}}
			>
				<IconComponent
					aria-hidden="true"
					sx={{
						fontSize: isMobile ? '0.875rem' : '1rem',
						color: isMobile ? 'common.black' : 'text.secondary',
						mt: isMobile ? 0 : 0.75
					}}
				/>
				<Typography
					variant="body-md"
					component="span"
					sx={{
						color: isMobile ? 'common.black' : 'text.secondary',
						fontSize: '1rem',
						fontWeight: 400,
						lineHeight: isMobile ? '1rem' : '1.5rem',
						letterSpacing: '-0.05px'
					}}
				>
					{displayText}
				</Typography>
			</Box>
		);
	};

	if (isMobile) {
		return (
			<Box component="li" sx={{ listStyle: 'none', width: '100%', justifySelf: 'stretch' }}>
				<ButtonBase
					onClick={() => setIsMobileExpanded((prev) => !prev)}
					aria-expanded={isMobileExpanded}
					aria-controls={panelId}
					disableRipple
					sx={{
						width: '100%',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						textAlign: 'left',
						px: 0,
						pt: 6,
						pb: 4,
						minHeight: 'unset',
						backgroundColor: 'transparent',
						color: 'common.black',
						WebkitTapHighlightColor: 'transparent',
						'&:hover, &:active, &:focusVisible': {
							backgroundColor: 'transparent'
						}
					}}
				>
					<Typography
						variant="body-md"
						component="h3"
						sx={{
							color: 'common.black',
							fontSize: '1rem',
							fontWeight: 500,
							lineHeight: '1.5rem',
							pr: 3
						}}
					>
						{clampedHeadline}
					</Typography>
					<ExpandMoreIcon
						aria-hidden="true"
						sx={{
							color: 'common.black',
							transform: isMobileExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
							transition: shouldReduceMotion ? 'none' : 'transform 300ms cubic-bezier(0.22, 1, 0.36, 1)'
						}}
					/>
				</ButtonBase>

				<Collapse in={isMobileExpanded} timeout={shouldReduceMotion ? 0 : 320} unmountOnExit>
					<Box id={panelId} sx={{ p: 0 }}>
						<Typography variant="body-md" sx={{ mt: 6, color: 'text.secondary' }}>
							{clampedSubline}
						</Typography>

						{address && (
							<Typography variant="body-md" sx={{ mt: 6, color: 'common.black', whiteSpace: 'pre-line' }}>
								{address}
							</Typography>
						)}

						{overlayRows.length ? (
							<Box
								component="ul"
								sx={{
									display: 'flex',
									flexDirection: 'column',
									gap: 3,
									m: 0,
									p: 0,
									mt: 6,
									listStyle: 'none'
								}}
							>
								{overlayRows.map(({ key, value, displayText, icon }) => (
									<Box key={key} component="li" sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
										{renderContactLink(key, value, displayText, icon)}
									</Box>
								))}
							</Box>
						) : null}

						{hasImage && (
							<Box
								sx={{
									position: 'relative',
									width: 361,
									maxWidth: '100%',
									height: 168,
									borderRadius: 2,
									overflow: 'hidden',
									mt: 6,
									mb: 6,
									mx: 'auto'
								}}
							>
								<Image
									src={logoSrc as string}
									alt={logoAlt}
									fill
									sizes="(max-width: 767px) 100vw, 361px"
									style={{ objectFit: 'cover', objectPosition: 'center' }}
								/>
							</Box>
						)}
					</Box>
				</Collapse>
				<Divider
					sx={{
						borderColor: (theme) => alpha(theme.palette.common.black, 0.18)
					}}
				/>
			</Box>
		);
	}

	const contactPanel = (address || overlayRows.length > 0) && (
		<>
			{address && (
				<Typography
					variant="body-md"
					sx={{
						color: 'text.secondary',
						lineHeight: '1.5rem',
						whiteSpace: 'pre-line'
					}}
				>
					{address}
				</Typography>
			)}

			<Box
				component="ul"
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: 2,
					m: 0,
					p: 0,
					mt: address ? 6 : 0,
					listStyle: 'none'
				}}
			>
				{overlayRows.map(({ key, value, displayText, icon }) => (
					<Box key={key} component="li" sx={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
						{renderContactLink(key, value, displayText, icon)}
					</Box>
				))}
			</Box>
		</>
	);

	return (
		<Card
			component="li"
			tabIndex={isHoverEnabled ? 0 : -1}
			aria-labelledby={headingId}
			onMouseEnter={isHoverEnabled ? () => setIsHovered(true) : undefined}
			onMouseLeave={isHoverEnabled ? () => setIsHovered(false) : undefined}
			onFocus={isHoverEnabled ? () => setIsHovered(true) : undefined}
			onBlur={
				isHoverEnabled
					? (e) => {
							if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsHovered(false);
						}
					: undefined
			}
			sx={{
				listStyle: 'none',
				width: '100%',
				maxWidth: 308,
				height: { xs: 'auto', md: 368 },
				borderRadius: 2,
				backgroundColor: 'common.white',
				boxShadow: 'none',
				display: 'flex',
				flexDirection: 'column',
				p: 8,
				'&:focusVisible': {
					outline: (theme) => `2px solid ${alpha(theme.palette.common.black, 0.35)}`,
					outlineOffset: '2px'
				}
			}}
		>
			<Typography
				id={headingId}
				variant="body-md"
				component="h3"
				sx={{ color: 'common.black', fontWeight: 500, lineHeight: '1.5rem' }}
			>
				{clampedHeadline}
			</Typography>

			<Typography variant="body-md" sx={{ mt: 6, color: 'text.secondary', lineHeight: '1.5rem' }}>
				{clampedSubline}
			</Typography>

			{hasImage ? (
				<Box
					sx={{
						position: 'relative',
						width: '100%',
						height: 204,
						aspectRatio: '5 / 4',
						mt: 6,
						overflow: 'hidden'
					}}
				>
					<motion.div
						animate={{
							opacity: isHovered && hasOverlayContent ? 0 : 1,
							y: isHovered && hasOverlayContent ? (shouldReduceMotion ? 0 : -4) : 0
						}}
						transition={{
							duration: shouldReduceMotion ? 0 : 0.42,
							ease: smoothEase
						}}
						aria-hidden="true"
						style={{
							position: 'absolute',
							inset: 0,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							pointerEvents: 'none'
						}}
					>
						<Box
							sx={{
								position: 'relative',
								width: 255,
								maxWidth: '100%',
								height: 204
							}}
						>
							<StrapiImageRenderer
								src={logoSrc}
								host={host}
								alt=""
								fill
								sizes="255px"
								style={{ objectFit: 'contain', objectPosition: 'center' }}
							/>
						</Box>
					</motion.div>

					{hasOverlayContent && (
						<motion.div
							animate={{
								opacity: isHovered ? 1 : 0,
								y: isHovered ? 0 : shouldReduceMotion ? 0 : 4
							}}
							transition={{
								duration: shouldReduceMotion ? 0 : 0.38,
								ease: smoothEase
							}}
							aria-hidden={!isHovered}
							style={{
								position: 'absolute',
								inset: 0,
								background: theme.palette.common.white,
								display: 'flex',
								flexDirection: 'column',
								paddingTop: 4,
								paddingBottom: 32,
								pointerEvents: isHovered ? 'auto' : 'none'
							}}
						>
							{contactPanel}
						</motion.div>
					)}
				</Box>
			) : (
				contactPanel && (
					<Box
						sx={{
							mt: 6,
							display: 'flex',
							flexDirection: 'column'
						}}
					>
						{contactPanel}
					</Box>
				)
			)}
		</Card>
	);
};

export default PartnerCard;
